import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Languages
import { useTranslation } from "react-i18next";

// CSS
import "./splitpayment.css";

// Components
import CreditCardDetails from "./subComponent/CreditCardDetails/creditcarddetails";

// Services
import { EMPTY_CARD, useCards } from "../../../../contexts/CardsContext";
import TotalPayment from "../TotalPayment";
import { useOrder } from "../../../../contexts/OrderContext";
import { App, Button, message } from "antd";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import Loading from "react-loading";
import { errorCodes } from "../../PriceOfferComponent/subComponents/PassangerData/passangerdata";
import {
  buildOrderReqDataForFlightsNewDynamic,
  isFlightsNewProposalPackage,
} from "../../../../utils/dynamicPackageDisplayMode";
import { resolveFlightsBankTransferOrderNumber } from "../../../../utils/orders/resolveFlightsSuccessOrderDisplay";
import { postFlightsNewProposalBankTransfer } from "../../../../services/dynamicService";
import { getUserInfo } from "../../../../services/localStorage";
import { resolveCustomerFacingHotelLineIlsFromOrder } from "../../../../utils/hotelBookingPriceSsot";
import { isHotelApiPriceOnlyOrder } from "../../../../utils/hotelApiPriceOnlyPipeline";
import { resolveLdsFinancialState } from "../../../../utils/orders/resolveLdsFinancialState";
import LdsPaymentFinancialSummary, {
  useLdsPaymentFinancialSummary,
} from "../LdsPaymentFinancialSummary";
import { sumLdsPaymentsCapturedIls } from "../../../../utils/orders/ldsSplitPaymentCards";
import { parseLdsOrderPayments } from "../../../../utils/orders/ldsSplitPaymentsSsot";
import { isHubwayzCheckoutOrder } from "../../../../utils/payment/isHubwayzCheckoutOrder";

function logHotelPaymentTotalRecalcTrace(payload) {
  if (typeof console !== "undefined" && typeof console.info === "function") {
    console.info("[HOTEL_PAYMENT_TOTAL_RECALC_TRACE]", payload);
  }
  try {
    // #region agent log
    fetch("http://127.0.0.1:7890/ingest/b0b67dfb-7306-4d13-82f5-46e25cc15ee5", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "e5c5ca",
      },
      body: JSON.stringify({
        sessionId: "e5c5ca",
        runId: "hotel-payment-total-recalc",
        hypothesisId: "T1,T2,T3,T4,T5",
        location: "src/Components/Agent/PaymentAgent/SplitPayment/splitpayment.jsx",
        message: "[HOTEL_PAYMENT_TOTAL_RECALC_TRACE]",
        data: payload,
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
  } catch {
    /* ignore debug logging */
  }
}

const SplitPayment = ({
  orderReqData,
  isDynamicOrder = false,
  setOrderReqData,
  setDynamicOrder,
  packageId,
  handlingFee,
  baseCommissions,
  cardFee,
  isNewOrder,
  getOrderData,
  totalPrice, // Total from PaymentAgent: cruise | flights_new_proposal | (optional override)
  isCruiseOrder = false,
  isFlightsNewProposal = false, // explicit flag from PaymentAgent when package is flight-only proposal
  isLdsPackageOrder = false,
  ldsBlockNewCreditLinks = false,
  orderId,
  isPublicCustomer = false,
  hideCardControlRow = false,
  /** When sticky owns אשראי/העברה (DP), hide duplicate radios here. */
  hideInlinePayModeChoice = false,
  /** Controlled pay mode from PaymentAgent sticky: "card" | "bank_transfer". */
  payMode: payModeControlled = null,
  onPayModeChange = null,
  paymentExchangeRate = 0,
  /** When set, this is already the remaining ILS. Do not subtract captured payments again. */
  priceToPayOverride = null,
  bookingExchangeRate = 0,
  blockHotelPaymentUntilRepriceConfirmed = false,
  repriceHotelBaseIls = null,
  paymentRepriceConfirmed = false,
  isTermsConfirmed = false,
  rejectedUnconvertedAmount = null,
}) => {
  const { notification } = App.useApp();
  const { currentHotelOrder, calcOurTotalPrice } = useOrder();
  const { ldsSupplement, linkedFlightsDealPrice, usdToIls } =
    useSelectedResult();

  // Translator
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { cards, updateCards, allCardsInterest, activeCardIndex } = useCards();

  const dynamicSuccessPath = (id) =>
    location.pathname.startsWith("/p/dashboard")
      ? `/p/dashboard/dynamic/success/${id}`
      : `/dynamic/success/${id}`;
  const [paymentLinks, setPaymentLinks] = useState([]);

  // States

  // const [priceToPay, setPriceToPay] = useState(
  //   parseFloat(currentHotelOrder.ourPrice.priceWithCreditCardFee - sumPaid)
  // );

  const sumPaid = useMemo(() => {
    if (isLdsPackageOrder && orderReqData) {
      const parsed = parseLdsOrderPayments(orderReqData);
      if (parsed.length > 0) {
        return sumLdsPaymentsCapturedIls(parsed, orderReqData);
      }
    }
    return cards.reduce((acc, cur) => {
      if (cur.paid) {
        return acc + cur.amountWithInterest;
      } else {
        return acc;
      }
    }, 0);
  }, [cards, isLdsPackageOrder, orderReqData]);

  const hotel_price = useMemo(() => {
    if (!currentHotelOrder) return 0;
    if (isHotelApiPriceOnlyOrder(currentHotelOrder)) return 0;
    const price_with_handling_fee = calcOurTotalPrice(currentHotelOrder);

    return (
      price_with_handling_fee?.priceWithHandleFee ||
      currentHotelOrder?.selectedHotelPackage?.ourPrice
        ?.priceWithCommissionAndCreditCardFee
    );
  }, [currentHotelOrder, calcOurTotalPrice, currentHotelOrder?.pricePipelineMode]);

  const flightsNewFromOrder = useMemo(
    () => isFlightsNewProposalPackage(orderReqData),
    [orderReqData]
  );
  const treatAsFlightsNewProposal =
    isFlightsNewProposal || flightsNewFromOrder;

  /** HotelsRiver DP + flights_new: credit / bank like vacation-packages payment. */
  const showDpPaymentMethodChoice =
    Boolean(isDynamicOrder && orderId) && !isLdsPackageOrder && !isCruiseOrder;
  const showFlightsNewBankFlow = showDpPaymentMethodChoice;
  const flightsBankAlreadyRecorded =
    orderReqData?.bank_transfer === true ||
    orderReqData?.clientsinfo?.hrFlightPackage?.payment_method ===
      "bank_transfer";

  const isPayModeControlled =
    payModeControlled === "card" || payModeControlled === "bank_transfer";
  const [flightsPayModeInternal, setFlightsPayModeInternal] = useState(
    flightsBankAlreadyRecorded ? "bank_transfer" : "card"
  );
  const flightsPayMode = isPayModeControlled
    ? payModeControlled
    : flightsPayModeInternal;
  const setFlightsPayMode = (mode) => {
    if (typeof onPayModeChange === "function") onPayModeChange(mode);
    if (!isPayModeControlled) setFlightsPayModeInternal(mode);
  };
  const [flightsBankSubmitting, setFlightsBankSubmitting] = useState(false);

  useEffect(() => {
    if (!flightsBankAlreadyRecorded) return;
    if (typeof onPayModeChange === "function") {
      onPayModeChange("bank_transfer");
    }
    if (!isPayModeControlled) {
      setFlightsPayModeInternal("bank_transfer");
    }
  }, [flightsBankAlreadyRecorded, isPayModeControlled, onPayModeChange]);

  const ldsFinancialSummary = useLdsPaymentFinancialSummary(
    orderReqData,
    isLdsPackageOrder
  );

  const ldsOrderTotal = useMemo(() => {
    if (!isLdsPackageOrder || !ldsFinancialSummary) return null;
    return ldsFinancialSummary.totalAmount;
  }, [isLdsPackageOrder, ldsFinancialSummary]);

  const ldsRemainingBalance = useMemo(() => {
    if (!isLdsPackageOrder || !ldsFinancialSummary) return null;
    return ldsFinancialSummary.remainingBalance;
  }, [isLdsPackageOrder, ldsFinancialSummary]);

  const ldsFullyPaid = Boolean(
    isLdsPackageOrder && ldsFinancialSummary?.isFullyPaid
  );

  const ldsNoNewPaymentLinks = Boolean(
    isLdsPackageOrder &&
      (ldsFullyPaid ||
        (ldsRemainingBalance != null && ldsRemainingBalance <= 0.02))
  );

  const full_price = useMemo(() => {
    if (isLdsPackageOrder && ldsOrderTotal != null && ldsOrderTotal > 0) {
      return Number(ldsOrderTotal);
    }

    if (isCruiseOrder && totalPrice) {
      return parseFloat(totalPrice);
    }

    // Flight-only proposal: parent already computed ILS total (flights + supplements + guaranty)
    if (treatAsFlightsNewProposal && totalPrice != null) {
      const n = parseFloat(totalPrice);
      return Number.isFinite(n) ? n : 0;
    }

    if (
      isDynamicOrder &&
      !treatAsFlightsNewProposal &&
      !isCruiseOrder &&
      totalPrice != null &&
      Number.isFinite(Number(totalPrice))
    ) {
      return Number(totalPrice);
    }

    // Standalone hotel checkout: parent PaymentAgent already computed full ILS (SSOT hotel + extras)
    if (
      !isCruiseOrder &&
      !treatAsFlightsNewProposal &&
      !isDynamicOrder &&
      totalPrice != null &&
      Number.isFinite(Number(totalPrice)) &&
      Number(totalPrice) > 0
    ) {
      return Number(totalPrice);
    }

    const supplements = Object.values(ldsSupplement || {}).reduce(
      (acc, curr) => acc + (curr?.checked ? curr.ourFare : 0),
      0
    );
    const hotelLineSsot = resolveCustomerFacingHotelLineIlsFromOrder(currentHotelOrder);
    const hotelIls =
      hotelLineSsot != null
        ? hotelLineSsot
        : usdToIls(hotel_price || 0);
    const price = parseFloat(
      hotelIls + (linkedFlightsDealPrice + supplements)
    );
    return price;
  }, [
    hotel_price,
    currentHotelOrder,
    linkedFlightsDealPrice,
    ldsSupplement,
    isCruiseOrder,
    totalPrice,
    usdToIls,
    treatAsFlightsNewProposal,
    isDynamicOrder,
    isLdsPackageOrder,
    ldsOrderTotal,
  ]);

  useEffect(() => {
    const firstCardInstallments =
      cards && cards.length > 0 ? Number(cards[0]?.numberofpayments || 1) : 1;
    const paymentLinkAmountIls =
      cards?.[0]?.numberofpayments > 1
        ? Number(cards?.[0]?.amountWithInterest || 0)
        : Number(cards?.[0]?.amount || 0);
    console.info("[CHECKOUT_UI_REDESIGN_AUDIT]", {
      orderId: orderId || orderReqData?.id || null,
      creditCardsCount: cards.length,
      installments: firstCardInstallments,
      total: Number(full_price || 0),
      layoutVersion: "payment-agent-v2-compact",
    });
    logHotelPaymentTotalRecalcTrace({
      source: "SplitPayment.full_price",
      orderId: orderId || orderReqData?.id || null,
      baseHotelPrice: null,
      handlingFee: Number(handlingFee || 0),
      installments: firstCardInstallments,
      installmentsFee: Math.max(
        0,
        Number(cards?.[0]?.amountWithInterest || 0) - Number(cards?.[0]?.amount || 0)
      ),
      resolvedTotalIls: Number(full_price || 0),
      paymentLinkAmountIls,
      totalPriceProp: Number(totalPrice || 0),
      cardsFirstAmount: Number(cards?.[0]?.amount || 0),
      cardsFirstAmountWithInterest: Number(cards?.[0]?.amountWithInterest || 0),
    });
  }, [
    orderId,
    orderReqData?.id,
    cards,
    full_price,
    handlingFee,
    totalPrice,
  ]);
  
  // Get original currency and GBP price from currentHotelOrder
  const originalCurrency = useMemo(() => {
    return currentHotelOrder?.ourPrice?.originalCurrency || 
           currentHotelOrder?.selectedHotelPackage?.ourPrice?.originalCurrency;
  }, [currentHotelOrder]);
  
  const finalPriceGBP = useMemo(() => {
    return currentHotelOrder?.ourPrice?.finalPriceGBP || 
           currentHotelOrder?.selectedHotelPackage?.ourPrice?.finalPriceGBP;
  }, [currentHotelOrder]);

  const priceToPay = useMemo(() => {
    const override = Number(priceToPayOverride);
    if (priceToPayOverride != null && Number.isFinite(override) && override >= 0) {
      return override;
    }
    if (isLdsPackageOrder && ldsRemainingBalance != null) {
      return Math.max(Number(ldsRemainingBalance), 0);
    }
    return parseFloat(full_price - sumPaid);
  }, [sumPaid, full_price, isLdsPackageOrder, ldsRemainingBalance, priceToPayOverride]);

  useEffect(() => {
    if (orderReqData?.paid) {
      if (
        orderReqData &&
        (!orderReqData.errors || orderReqData.errors.length === 0)
      ) {
        navigate(
          isDynamicOrder || isFlightsNewProposal
            ? dynamicSuccessPath(orderReqData.id)
            : "/p/dashboard/success/" + orderReqData.id
        );
      }
    }
  }, [
    priceToPay,
    orderReqData,
    isDynamicOrder,
    isFlightsNewProposal,
    navigate,
    location.pathname,
  ]);

  const [paymentLinksTotalToPay, setPaymentLinksTotalToPay] = useState(0);

  useEffect(() => {
    if (orderReqData) {
      const parsedPayments = parseLdsOrderPayments(orderReqData);
      if (parsedPayments.length > 0) {
        setPaymentLinks(parsedPayments);
        setPaymentLinksTotalToPay(
          isLdsPackageOrder
            ? sumLdsPaymentsCapturedIls(parsedPayments, orderReqData)
            : parsedPayments
                .filter((payment) => payment.paid === true)
                .reduce((acc, cur) => acc + cur.actualAmount, 0)
        );

        if (!isLdsPackageOrder) {
          const tmpCards = cards.slice(0, parsedPayments.length);
          if (tmpCards.length < parsedPayments.length) {
            for (let i = tmpCards.length; i < parsedPayments.length; i++) {
              tmpCards.push({ ...EMPTY_CARD });
            }
          }
          for (let i = 0; i < parsedPayments.length; i++) {
            tmpCards[i].amount = parsedPayments[i].actualAmount;
            tmpCards[i].amountWithInterest = parsedPayments[i].amount;
            tmpCards[i].numberofpayments = parsedPayments[i].tash;
            tmpCards[i].paid = parsedPayments[i].paid;
          }
          updateCards(tmpCards);
        }
      } else {
        setPaymentLinks([]);
        setPaymentLinksTotalToPay(0);
        if (!isLdsPackageOrder) {
          const keepAgentAllocation =
            isHubwayzCheckoutOrder(orderReqData) &&
            (cards.length > 1 || cards.some((card) => card?.principalTouched));
          if (!keepAgentAllocation) {
            updateCards([
              {
                ...EMPTY_CARD,
                amount: Number(full_price || 0),
                amountWithInterest: Number(full_price || 0),
              },
            ]);
          }
        }
      }
      if (
        orderReqData.errors &&
        orderReqData.errors.length > 0 &&
        orderReqData.status !== "OK"
      ) {
        // Collect unique error codes, flattening nested arrays if present
        const flattenedErrors = orderReqData.errors.flat
          ? orderReqData.errors.flat()
          : [].concat(...orderReqData.errors);
        const uniqueCodes = Array.from(
          new Set(
            flattenedErrors.map(
              (error) => error.code || error.ErrorCode || "NO_CODE"
            )
          )
        );
        uniqueCodes.forEach((code) => {
          if (code === "NO_CODE") return;
          const messageText = errorCodes[code];
          if (messageText) {
            notification.error({
              message: treatAsFlightsNewProposal
                ? "שגיאה בטיפול בהזמנה"
                : "שגיאה בסגירת המלון",
              description: messageText,
              duration: 0,
              placement: "bottomLeft",
            });
          }
        });
        notification.info({
          message: treatAsFlightsNewProposal
            ? "ישנן שגיאות בהזמנה, יש ליצור קשר עם מחלקת אופרציה"
            : "ישנן שגיאות בסגירת המלון, יש ליצור קשר עם מחלקת אופרציה",
          duration: 0,
          placement: "bottomLeft",
        });
      } else if (resolveLdsFinancialState(orderReqData).shouldNavigateSuccess) {
        if (isDynamicOrder || isFlightsNewProposal) {
          navigate(dynamicSuccessPath(orderReqData.id));
        } else {
          navigate("/p/dashboard/success/" + orderReqData.id);
        }
      }
    }
  }, [
    orderReqData,
    treatAsFlightsNewProposal,
    isDynamicOrder,
    isFlightsNewProposal,
    navigate,
    location.pathname,
    full_price,
  ]);

  const bankTransferOrderNumber = useMemo(
    () => resolveFlightsBankTransferOrderNumber(orderReqData, orderReqData),
    [orderReqData]
  );

  const confirmFlightsNewBankTransfer = async () => {
    if (!isTermsConfirmed) return;
    const agentId = getUserInfo()?.id;
    if (agentId == null || agentId === "") {
      message.error("לא נמצא מזהה סוכן — התחברו מחדש.");
      return;
    }
    setFlightsBankSubmitting(true);
    try {
      const res = await postFlightsNewProposalBankTransfer({
        dynamicOrderId: orderId,
        agentID: agentId,
      });
      const data = res?.data;
      if (!data || data.status !== "ok") {
        throw new Error(data?.error || "שמירת בקשת ההעברה נכשלה");
      }
      const row = data.order;
      const built = buildOrderReqDataForFlightsNewDynamic(
        row,
        row?.flights || {}
      );
      setOrderReqData(built);
      if (setDynamicOrder) setDynamicOrder(row);
      navigate(dynamicSuccessPath(orderId));
    } catch (err) {
      let payload = err?.response?.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch {
          /* ignore */
        }
      }
      const msg =
        (payload && payload.error) ||
        err?.message ||
        "שמירת בקשת ההעברה נכשלה";
      message.error(String(msg));
    } finally {
      setFlightsBankSubmitting(false);
    }
  };

  const handleCCNumChange = (e) => {
    const newValue = parseInt(e.target.value);

    if (ldsNoNewPaymentLinks) {
      return message.warning("ההזמנה שולמה במלואה — לא ניתן להוסיף כרטיסי תשלום");
    }

    // Check if the entered value is greater than or equal to 1
    if (!isNaN(newValue) && newValue >= 1) {
      let tempCards = [];
      if (isLdsPackageOrder) {
        const lockedCount = cards.filter(
          (c) => c?.ldsSlotLocked || c?.paid
        ).length;
        if (newValue < lockedCount) {
          return message.error(
            "לא ניתן להפחית את מספר הכרטיסים מתחת לכרטיסים שכבר שולמו"
          );
        }
      }
      if (cards.length > newValue) {
        if (paymentLinks.length > newValue)
          return message.error(
            "לא ניתן להפחית את מספר הכרטיסים מתחת לכמות הקישורים לתשלום אשר נוצרו"
          );
        tempCards = cards.slice(0, newValue);
      } else {
        if (paymentLinksTotalToPay < priceToPay) {
          tempCards = [...cards]; // Create a new array copy
          for (let i = cards.length; i < newValue; i++) {
            tempCards.push({ ...EMPTY_CARD });
          }
        } else return message.warning("לא נשאר יותר כסף לתשלום");
      }
      // tempCards[0].amount = currentHotelOrder.ourPrice.priceWithCreditCardFee;

      updateCards(tempCards);
    } else {
      // If the entered value is less than 1 or not a valid number, set it to 1
      updateCards([EMPTY_CARD]);
    }
  };

  return (
    <>
      <div className="splitPayment-wrapper splitPayment-sub-wrapper">
        {showFlightsNewBankFlow && !hideInlinePayModeChoice && (
          <div
            className="tw-mb-4 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-neutral-50 tw-p-4"
            role="group"
            aria-label="אמצעי תשלום"
          >
            <div className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-neutral-800">
              {treatAsFlightsNewProposal && !isPublicCustomer
                ? "אמצעי תשלום (הצעת טיסות בלבד)"
                : "אמצעי תשלום"}
            </div>
            <div className="tw-flex tw-flex-wrap tw-gap-4">
              <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm">
                <input
                  type="radio"
                  name="flights-new-pay-mode"
                  checked={flightsPayMode === "card"}
                  disabled={flightsBankAlreadyRecorded}
                  onChange={() => setFlightsPayMode("card")}
                />
                כרטיס אשראי
              </label>
              <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm">
                <input
                  type="radio"
                  name="flights-new-pay-mode"
                  checked={flightsPayMode === "bank_transfer"}
                  onChange={() => setFlightsPayMode("bank_transfer")}
                />
                העברה בנקאית
              </label>
            </div>
          </div>
        )}

        {showFlightsNewBankFlow && flightsPayMode === "bank_transfer" && (
          <div
            className="tw-mb-4 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-neutral-50 tw-p-4 tw-space-y-3 tw-text-sm tw-leading-relaxed tw-text-neutral-700"
            data-dp-bank-transfer-panel="1"
          >
            <p>
              {treatAsFlightsNewProposal
                ? "נבחר תשלום בהעברה בנקאית. לא ייפתח תשלום אשראי ולא תבוצע הזמנה סופית אוטומטית — מדובר בהצעה (PROPOSAL) עד לזיהוי התשלום וטיפול ידני במשרד. מסך זה אינו מציג אישור הזמנה סופית מול ספק הטיסות."
                : "נבחר תשלום בהעברה בנקאית. לאחר אישור הבקשה יטופל הזיהוי במשרד — פרטי החשבון יוצגו / יישלחו בהתאם לנוהל HotelsRiver."}
            </p>
            <p>
              <strong>סכום לתשלום:</strong>{" "}
              <span className="tw-font-mono">
                ₪{Number(full_price || 0).toLocaleString("he-IL")}
              </span>
            </p>
            {bankTransferOrderNumber ? (
              <p>
                <strong>מספר הזמנה (מערכת):</strong>{" "}
                <span className="tw-font-mono">{bankTransferOrderNumber}</span>
              </p>
            ) : null}
            <p className="tw-text-neutral-600">
              פרטי חשבון בנק (מספר בנק / סניף / חשבון): מתקבלים ממחלקת חשבונאות
              או משרד התפעול — יש לציין את מספר ההזמנה בהערות להעברה. לאחר זיהוי
              ההעברה יימשך אישור ההזמנה באופן ידני.
            </p>
            {flightsBankAlreadyRecorded ? (
              <Button
                type="default"
                onClick={() => navigate("/dynamic/success/" + orderId)}
              >
                מעבר למסך סיכום
              </Button>
            ) : (
              <Button
                type="primary"
                loading={flightsBankSubmitting}
                disabled={!isTermsConfirmed || flightsBankSubmitting}
                onClick={confirmFlightsNewBankTransfer}
              >
                אישור ושמירת בקשת תשלום בהעברה
              </Button>
            )}
          </div>
        )}

        {/* Set Credit Card Number */}
        <div
          className={`splitPayment-card-amount-wrapper-wrapper splitPayment-control-row ${
            hideCardControlRow ? "splitPayment-control-row--hidden-proxy" : ""
          }`}
        >
          <div className="splitPayment-card-amount-wrapper splitPayment-control-row-inner">
            <div className="splitPayment-control-total">
              {isLdsPackageOrder && ldsFinancialSummary ? (
                <LdsPaymentFinancialSummary
                  summary={ldsFinancialSummary}
                  orderReqData={orderReqData}
                  variant="compact"
                  className="splitPayment-control-total-lds"
                />
              ) : (
                <>
                  <span className="splitPayment-control-total-label">סה״כ לתשלום:</span>
                  <span id="priProblem-total" className="splitPayment-control-total-value">
                    <TotalPayment
                      totalPrice={full_price}
                      originalCurrency={isCruiseOrder ? "ILS" : originalCurrency}
                      displayCurrency={isCruiseOrder ? "ILS" : undefined}
                      finalPriceGBP={isCruiseOrder ? null : finalPriceGBP}
                    />
                  </span>
                </>
              )}
            </div>

            <div className="splitPayment-card-amount-button splitPayment-control-tabs-wrap">
              <label className="splitPayment-card-amount-title">
                מספר כרטיסי אשראי לתשלום
              </label>
              <div
                className="splitPayment-card-count-tabs"
                role="tablist"
                aria-label={t("paymentagent.setnumcard")}
              >
                {[1, 2, 3, 4].map((count) => (
                  <button
                    key={`card-count-tab-${count}`}
                    type="button"
                    data-card-count-tab={count}
                    role="tab"
                    aria-selected={cards.length === count}
                    className={`splitPayment-card-count-tab ${
                      cards.length === count ? "is-active" : ""
                    }`}
                    onClick={() =>
                      handleCCNumChange({ target: { value: String(count) } })
                    }
                    disabled={
                      ldsNoNewPaymentLinks ||
                      (showFlightsNewBankFlow && flightsPayMode === "bank_transfer")
                    }
                  >
                    {count}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {!isDynamicOrder && isNewOrder && packageId && (
          <button
            className="passangerData-submit-button-pay-later"
            onClick={() => {
              navigate("/p/dashboard/searchoffer/" + packageId);
            }}
          >
            {t("paymentagent.backToReserve")}
          </button>
        )}

        {/* Credit Card Displayer */}
        <div className="splitPayment-card-displayer">
          {showFlightsNewBankFlow && flightsPayMode === "bank_transfer" ? null : !isLdsPackageOrder &&
            (full_price == null ||
              !Number.isFinite(full_price) ||
              full_price <= 0) ? (
            <div
              className="tw-flex tw-justify-center tw-items-center tw-h-full tw-w-full"
              style={{ minHeight: "500px" }}
            >
              <Loading type="spin" color="#000" className="loading-spinner" />
            </div>
          ) : isLdsPackageOrder && cards.length === 0 ? (
            <div
              className="tw-flex tw-justify-center tw-items-center tw-h-full tw-w-full"
              style={{ minHeight: "500px" }}
            >
              <Loading type="spin" color="#000" className="loading-spinner" />
            </div>
          ) : (
            cards.map((card, idx) => (
              <div key={`card-${idx}-${cards.length}`}>
                <CreditCardDetails
                  cardNumber={idx + 1}
                  cardIndex={idx}
                  isActiveCard={isLdsPackageOrder ? idx === activeCardIndex : idx === 0}
                  orderReqData={orderReqData}
                  setOrderReqData={setOrderReqData}
                  setDynamicOrder={setDynamicOrder}
                  paymentLink={
                    idx < paymentLinks.length ? paymentLinks[idx] : null
                  }
                  handlingFee={handlingFee}
                  baseCommissions={baseCommissions}
                  priceToPay={priceToPay}
                  paymentLinksTotalToPay={paymentLinksTotalToPay}
                  cardFee={cardFee}
                  getOrderData={getOrderData}
                  orderId={orderId} // Pass orderId for cruise orders
                  paymentExchangeRate={paymentExchangeRate}
                  bookingExchangeRate={bookingExchangeRate}
                  isLdsPackageOrder={isLdsPackageOrder}
                  ldsBlockNewCreditLinks={ldsBlockNewCreditLinks}
                  ldsNoNewPaymentLinks={ldsNoNewPaymentLinks}
                  blockHotelPaymentUntilRepriceConfirmed={
                    blockHotelPaymentUntilRepriceConfirmed
                  }
                  repriceHotelBaseIls={repriceHotelBaseIls}
                  paymentRepriceConfirmed={paymentRepriceConfirmed}
                  isPublicCustomer={isPublicCustomer}
                  rejectedUnconvertedAmount={rejectedUnconvertedAmount}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default SplitPayment;
